import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import { Check, Loader2, ShoppingBasket, Sparkles } from "lucide-react";

const qsDoneKey = (uid) => `mealpill_quick_start_done_${uid || "anon"}`;

export default function QuickStartPage() {
  const { api, user } = useAuth();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);

  const nameKey = lang === "uk" ? "ua" : lang;
  const uid = user?.id || user?.user_id;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api().get("/quick-start/catalog");
        const list = res.data?.items || [];
        if (!alive) return;
        setItems(list);
        setSelected(new Set(list.filter((i) => i.preselect).map((i) => i.name.ua)));
        const unresolved = list.filter((i) => !i.resolved).map((i) => i.name.ua);
        if (unresolved.length) console.warn("[QuickStart] unresolved in catalog:", unresolved);
      } catch (e) {
        console.error("[QuickStart] catalog load failed", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [api]);

  const groups = useMemo(() => {
    const g = [];
    const idx = {};
    for (const it of items) {
      const c = it.category || "other";
      if (!(c in idx)) { idx[c] = g.length; g.push({ category: c, items: [] }); }
      g[idx[c]].items.push(it);
    }
    return g;
  }, [items]);

  const toggle = (ua) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(ua) ? next.delete(ua) : next.add(ua);
      return next;
    });
  };

  const markDone = () => {
    try { localStorage.setItem(qsDoneKey(uid), "true"); } catch (_) {}
  };

  const handleGenerate = async () => {
    if (submitting || selected.size === 0) return;
    setSubmitting(true);
    try {
      await api().post("/fridge/bulk", { selected: Array.from(selected) });
      markDone();
      navigate("/fridge");
    } catch (e) {
       console.error("[QuickStart] save failed", e);
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    markDone();
    navigate("/");
  };

  const catLabel = (c) => {
    const k = `cat_${c}`;
    const v = t(k);
    return v && v !== k ? v : c;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  const count = selected.size;

  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      <div className="max-w-2xl mx-auto px-4 pt-8">
        <div className="flex items-center gap-2 mb-1">
          <ShoppingBasket className="w-6 h-6 text-emerald-500" />
          <h1 className="text-2xl font-bold text-gray-900">{t("qs_title")}</h1>
        </div>
        <p className="text-gray-500 mb-6">{t("qs_subtitle")}</p>

        {groups.map((group) => (
          <div key={group.category} className="mb-6">
            <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-2">
              {catLabel(group.category)}
            </h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {group.items.map((it) => {
                const ua = it.name.ua;
                const on = selected.has(ua);
                return (
                  <button
                    key={ua}
                    onClick={() => toggle(ua)}
                    className={`relative flex flex-col items-center justify-center gap-1 rounded-xl border p-3 text-center transition ${
                      on ? "border-emerald-500 bg-emerald-50" : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    {on && (
                      <span className="absolute top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500">
                        <Check className="h-3 w-3 text-white" />
                      </span>
                    )}
                    <span className="text-2xl leading-none">{it.icon}</span>
                    <span className="text-xs font-medium text-gray-700 leading-tight">
                      {it.name[nameKey] || it.name.ua}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="fixed bottom-0 inset-x-0 border-t border-gray-200 bg-white/95 backdrop-blur px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button onClick={handleSkip} className="text-sm text-gray-500 hover:underline whitespace-nowrap">
            {t("qs_skip")}
          </button>
          <button
            onClick={handleGenerate}
            disabled={count === 0 || submitting}
            className={`flex-1 flex items-center justify-center gap-2 rounded-xl py-3 font-semibold text-white transition ${
              count === 0 || submitting ? "bg-gray-300 cursor-not-allowed" : "bg-emerald-500 hover:bg-emerald-600"
            }`}
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (
              <>
                <Sparkles className="w-5 h-5" />
                {t("qs_generate")} {count > 0 ? `(${count})` : ""}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}